// =============================================================================
// Password strength — Weak / Fair / Strong
//
// These are the conventional labels, deliberately. The scale started out as
// Weak / Decent / Secure, which nobody else uses: "Strong" is the near-universal
// top-tier word, and "Fair" is what zxcvbn calls its middle band — so if this
// ever swaps to zxcvbn-ts (see below) the vocabulary already lines up and users
// never see the wording change twice. "Secure" was also the wrong promise to
// make: it describes the password, not the system, and this rating cannot speak
// to the latter.
//
// Used by the client-side encryption password dialog. That secret wraps the KEK
// protecting every note cached on the device, so the rating has to be honest:
// telling someone "Strong" for `Forensic1!` would be worse than showing nothing.
//
// WHY THIS AND NOT A CHARACTER-CLASS CHECKLIST
// The usual "has upper + lower + digit + symbol" rule rates `P@ssw0rd1` as
// strong and `correct horse battery staple` as weak, which is backwards. This
// scores what actually costs an attacker guesses:
//   - LENGTH dominates. Each extra character multiplies the search space;
//     adding a symbol to a short password barely moves it.
//   - Character-class variety widens the per-character alphabet (a secondary
//     term, not the headline).
//   - Then PENALTIES for the patterns cracking tools try first: known-common
//     passwords, a single repeated character, keyboard runs, digit sequences,
//     dates, and anything built from the user's own email or name.
//
// IF YOU WANT REAL-WORLD ACCURACY, SWAP IN zxcvbn
// zxcvbn-ts (the maintained TypeScript port of Dropbox's zxcvbn) does the same
// job with real dictionaries — English words, names, leaked-password lists —
// and returns an estimated guess count rather than a heuristic score. It is the
// right upgrade if this ever guards more than a local cache; the cost is a
// dependency plus ~400 kB of dictionaries, which wants lazy-loading in a PWA.
// Everything above the `evaluate()` boundary is UI, so switching is a
// one-function change: keep the signature, map zxcvbn's 0-4 score onto these
// three levels.
// =============================================================================

export type PasswordStrengthLevel = 'weak' | 'fair' | 'strong';

export interface PasswordStrengthResult {
  level: PasswordStrengthLevel;
  label: string;      // "Weak" | "Fair" | "Strong"
  percent: number;    // 0-100, for the meter bar
  advice: string;     // the single most useful next step, or a confirmation
}

// The passwords tried first by every cracking tool, plus the ones this product
// invites. Deliberately short: a real dictionary is zxcvbn's job, and a long
// list here would bloat the bundle for little gain.
const COMMON_PASSWORDS: string[] = [
  'password', 'passw0rd', 'p@ssword', 'p@ssw0rd', 'welcome', 'letmein', 'admin',
  'administrator', 'qwerty', 'qwertyuiop', 'asdfgh', 'zxcvbn', 'iloveyou',
  'monkey', 'dragon', 'sunshine', 'princess', 'football', 'baseball', 'master',
  'shadow', 'superman', 'trustno1', 'starwars', 'whatever', 'changeme',
  'secret', 'access', 'login', 'abc123', '123456', '1234567', '12345678',
  '123456789', '1234567890', '111111', '000000', '121212', '654321',
  'forensic', 'forensicnotes', 'notes', 'evidence', 'police', 'detective',
];

const KEYBOARD_RUNS: string[] = [
  'qwertyuiop', 'asdfghjkl', 'zxcvbnm', '1234567890', 'abcdefghijklmnopqrstuvwxyz',
];

export default class PasswordStrength {
  // ===========================================================================
  // Rate a password.
  //
  // `userInputs` are things an attacker targeting THIS user already knows —
  // their email and name. A password built from them is weak no matter how
  // many symbols it carries.
  // ===========================================================================
  static evaluate(password: string, userInputs: string[] = []): PasswordStrengthResult {
    const pwd = password || '';

    if (pwd.length === 0) {
      return { level: 'weak', label: '', percent: 0, advice: '' };
    }

    const lower = pwd.toLowerCase();

    // --- primary term: length -------------------------------------------------
    // Scored on a curve, not linearly: the jump from 8 to 12 characters matters
    // far more than 20 to 24.
    let score = Math.min(52, pwd.length * 4);

    // --- secondary term: alphabet width --------------------------------------
    const classes =
      (/[a-z]/.test(pwd) ? 1 : 0) +
      (/[A-Z]/.test(pwd) ? 1 : 0) +
      (/[0-9]/.test(pwd) ? 1 : 0) +
      (/[^a-zA-Z0-9]/.test(pwd) ? 1 : 0);

    score += (classes - 1) * 9;

    // Distinct characters: "aaaaaaaaaaaa" is long but trivial.
    const distinct = new Set(pwd).size;
    score += Math.min(12, distinct * 1.2);

    // --- penalties: what gets guessed first ----------------------------------
    let capped: number = null;   // hard ceiling — some flaws can't be out-scored
    let advice = '';

    if (PasswordStrength.matchesCommon(lower)) {
      capped = 20;
      advice = 'This is one of the first passwords an attacker tries. Choose something unrelated to common words.';
    } else if (PasswordStrength.containsUserInput(lower, userInputs)) {
      capped = 30;
      advice = 'Avoid your own name or email — anyone targeting you starts there.';
    } else if (distinct <= 2) {
      capped = 20;
      advice = 'Repeating a couple of characters is as guessable as a short password.';
    } else if (PasswordStrength.hasRun(lower, 4)) {
      capped = 35;
      advice = 'Sequences like "1234" or "qwerty" are tried early — break the pattern.';
    } else if (/^\d+$/.test(pwd)) {
      capped = 30;
      advice = 'Digits alone leave only ten options per character. Add letters and symbols.';
    }

    if (capped !== null) {
      score = Math.min(score, capped);
    }

    score = Math.max(0, Math.min(100, Math.round(score)));

    // --- level ---------------------------------------------------------------
    // Length gates the top band on purpose: 12+ characters is the difference
    // between a cache that can be brute-forced and one that cannot, so a short
    // password never reads "Strong" however exotic its symbols.
    let level: PasswordStrengthLevel;

    // A long passphrase ("correct horse battery staple") is stronger than a
    // short scramble, so raw length can reach the top band on its own - the
    // character-class requirement only applies to shorter passwords.
    const isLongPassphrase = pwd.length >= 20 && distinct >= 10;

    if (capped === null && (isLongPassphrase || (score >= 75 && pwd.length >= 12 && classes >= 3))) {
      level = 'strong';
    } else if (score >= 50 && pwd.length >= 8 && capped === null) {
      level = 'fair';
    } else {
      level = 'weak';
    }

    // --- the one thing worth saying next -------------------------------------
    if (!advice) {
      if (level === 'strong') {
        advice = 'Store it in your password manager — it cannot be recovered.';
      } else if (pwd.length < 12) {
        advice = 'Length helps most: aim for 12 characters or more.';
      } else if (classes < 3) {
        advice = 'Mix in upper case, numbers or symbols.';
      } else {
        advice = 'Nearly there — a few more characters would make this strong.';
      }
    }

    return {
      level: level,
      label: level === 'strong' ? 'Strong' : level === 'fair' ? 'Fair' : 'Weak',
      percent: level === 'weak' ? Math.max(12, Math.min(33, score)) : level === 'fair' ? Math.max(40, Math.min(66, score)) : Math.max(75, score),
      advice: advice,
    };
  }

  // A common password, with the obvious leet substitutions undone so that
  // "p4ssw0rd" is caught by the "password" entry.
  private static matchesCommon(lower: string): boolean {
    const deLeet = lower
      .replace(/[@4]/g, 'a')
      .replace(/[0]/g, 'o')
      .replace(/[1!|]/g, 'l')
      .replace(/[3]/g, 'e')
      .replace(/[5$]/g, 's')
      .replace(/[7]/g, 't');

    // Strip trailing digits/punctuation: "Password123!" is "password".
    const stripped = deLeet.replace(/[^a-z]+$/g, '');

    return COMMON_PASSWORDS.some((c) => stripped === c || deLeet === c || (c.length >= 6 && deLeet.indexOf(c) !== -1));
  }

  private static containsUserInput(lower: string, userInputs: string[]): boolean {
    return (userInputs || []).some((raw) => {
      if (!raw) {
        return false;
      }

      // Compare against the email local-part and each name token.
      return raw
        .toLowerCase()
        .split(/[@.\s_-]+/)
        .filter((part) => part.length >= 4)
        .some((part) => lower.indexOf(part) !== -1);
    });
  }

  // A forward or reverse run of `len` characters from a keyboard row, the
  // alphabet or the digits.
  private static hasRun(lower: string, len: number): boolean {
    return KEYBOARD_RUNS.some((row) => {
      const reversed = row.split('').reverse().join('');

      for (let i = 0; i + len <= lower.length; i++) {
        const chunk = lower.substr(i, len);

        if (row.indexOf(chunk) !== -1 || reversed.indexOf(chunk) !== -1) {
          return true;
        }
      }

      return false;
    });
  }
}
